import { forwardRef } from "react";

import BaseTrigger from "@/components_v2/Trigger/BaseTrigger";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import RoleBadgeTag from "@/components_v2/Tags/RoleBadgeTag";
import UserAvatar from "@/domains/users/components/UserAvatar";
import { getUserRoleBadgeName } from "@/domains/users/getUserRoleBadgeName";
import { SIDEBAR_TRIGGER_VARIANT } from "../types";
import type { AccountSidebarTriggerProps } from "./types";

import "./styles.css";

const AccountSidebarTrigger = forwardRef<
  HTMLButtonElement,
  AccountSidebarTriggerProps
>(
  (
    {
      currentUser,
      isCollapsed,
      variant = SIDEBAR_TRIGGER_VARIANT.SIDEBAR,
      isOpen,
      className,
      ...buttonProps
    },
    ref,
  ) => {
    const currentUserRole = getUserRoleBadgeName(currentUser);
    const isHeader = variant === SIDEBAR_TRIGGER_VARIANT.HEADER;
    const showMeta = !isHeader && !isCollapsed;

    return (
      <BaseTrigger
        ref={ref}
        aria-label={currentUser.fullName}
        aria-expanded={isOpen}
        className={`account-trigger flex items-center gap-200
          ${isCollapsed && !isHeader ? "account-trigger-collapsed" : ""}
          ${!isHeader && !isCollapsed ? "account-trigger-expanded" : ""}
          ${isHeader ? "account-trigger--header" : ""}
          ${className ?? ""}`}
        {...buttonProps}
      >
        <span className="account-trigger-avatar-slot flex flex-shrink-0 justify-center items-center">
          {isHeader ? (
            <span className="account-trigger-avatar">
              <UserAvatar user={currentUser} />
            </span>
          ) : (
            <BaseTooltip
              title={currentUser.fullName}
              placement="right"
              {...(isCollapsed ? {} : { open: false })}
            >
              <span className="account-trigger-avatar">
                <UserAvatar user={currentUser} />
              </span>
            </BaseTooltip>
          )}
        </span>

        {showMeta ? (
          <div className="account-trigger-meta flex flex-col gap-50 flex-1 min-w-0">
            <div className="flex items-center gap-50">
              <p className="account-trigger-name-text body-m-medium one-line-ellipsis">
                <strong>{currentUser.fullName}</strong>
              </p>

              {currentUserRole && <RoleBadgeTag role={currentUserRole} />}
            </div>

            {currentUser.email ? (
              <p className="account-trigger-muted body-s-regular one-line-ellipsis">
                {currentUser.email}
              </p>
            ) : null}
          </div>
        ) : null}
      </BaseTrigger>
    );
  },
);

AccountSidebarTrigger.displayName = "AccountSidebarTrigger";

export default AccountSidebarTrigger;
